import { Fragment, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { makeStyles } from "tss-react/mui";
import { MiAlert, MiAvatar, MiModal } from "@miplatform/components";
import {
  BUTTON_VARIANTS,
  COLORS,
  DATE_TIME_FORMATS,
} from "@miplatform/constants";
import { formatDate, getContrastColor, getDate } from "@miplatform/utils";
import { mdiPencilOutline } from "@mdi/js";
import { QuantityControl } from "@mibuildweb/components";
import { Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useUserCapacities } from "../../../../../hooks/useUserCapacities/useUserCapacities";
import { useViewConfig } from "../../ViewConfigContext";

export const EditCapacityModal = ({
  open,
  onClose,
  user,
  calendarDate,
  onRefresh,
}) => {
  const { classes } = useStyles();
  const { viewConfig } = useViewConfig();

  const [orderTemplateCapacityCounts, setOrderTemplateCapacityCounts] =
    useState({});
  const isIncrementDisabled = useMemo(() => {
    const totalCapacity = Object.values(orderTemplateCapacityCounts).reduce(
      (sum, capacity) => sum + capacity,
      0
    );

    const today = getDate();
    const wholeHours = getDate(
      `${today.format(DATE_TIME_FORMATS.FORMAT_YYYY_MM_DD)} ${
        viewConfig.dayEndTime
      }`
    ).diff(
      getDate(
        `${today.format(DATE_TIME_FORMATS.FORMAT_YYYY_MM_DD)} ${
          viewConfig.dayStartTime
        }`
      ),
      "hours"
    );

    return totalCapacity >= wholeHours;
  }, [orderTemplateCapacityCounts, viewConfig]);
  const handleClose = () => {
    setOrderTemplateCapacityCounts({});
    onClose();
  };

  useEffect(() => {
    const localOrderTemplateCapacityCounts = {};

    if (user && user.capacities && viewConfig.orderTemplates) {
      const capacities =
        user.capacities[
          calendarDate?.format(DATE_TIME_FORMATS.FORMAT_YYYY_MM_DD)
        ];

      viewConfig.orderTemplates.forEach((ot) => {
        const orderTemplateCapacitiy = capacities?.find(
          (c) => c.orderTemplateId === ot.id
        );
        localOrderTemplateCapacityCounts[ot.id] =
          orderTemplateCapacitiy?.amount ?? 0;
      });
    }
    setOrderTemplateCapacityCounts(localOrderTemplateCapacityCounts);
  }, [viewConfig, user, open]);

  const handleDecreaseQty = (e, orderTemplate) => {
    e.stopPropagation();
    setOrderTemplateCapacityCounts((prev) => {
      const current = prev[orderTemplate.id] ?? 0;
      return {
        ...prev,
        [orderTemplate.id]: Math.max(0, current - 1),
      };
    });
  };

  const handleIncreaseQty = (e, orderTemplate) => {
    e.stopPropagation();
    setOrderTemplateCapacityCounts((prev) => {
      const current = prev[orderTemplate.id] ?? 0;
      return {
        ...prev,
        [orderTemplate.id]: Math.max(0, current + 1),
      };
    });
  };

  const { updateDailyUserCapacities } = useUserCapacities();

  const handleSubmit = async () => {
    const data = {
      calendarSetId: viewConfig.calendarSetId,
      userId: user.id,
      date: moment(calendarDate).format(DATE_TIME_FORMATS.FORMAT_YYYY_MM_DD),
      capacities: Object.entries(orderTemplateCapacityCounts).map((c) => {
        return {
          orderTemplateId: c[0],
          amount: c[1],
        };
      }),
    };

    await updateDailyUserCapacities(data);
    onRefresh();
    handleClose();
  };

  return (
    <MiModal
      open={open}
      closeModal={handleClose}
      title={`Edit ${user.name}'s Capacity`}
      description={`Edit the capacity for each type of work for ${formatDate(
        calendarDate,
        DATE_TIME_FORMATS.FORMAT_MM_DD_YYYY
      )}`}
      titleIcon={mdiPencilOutline}
      width={500}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          color: COLORS.BLACK,
          onClick: handleClose,
          width: 75,
        },
        {
          name: "Confirm",
          variant: BUTTON_VARIANTS.CONTAINED,
          onClick: handleSubmit,
          width: 100,
        },
      ]}
    >
      <Box maxHeight={460} overflow="auto">
        <MiAlert
          hideCloseButton
          isOpen={isIncrementDisabled}
          variant="warning"
          title="No Remaining Capacity"
          description="This user has reached their maximum capacity for this calendar."
          sx={{ py: 1, px: 2, mb: 1 }}
        />
        <Box className={classes.modalContent}>
          <Box>
            {viewConfig.orderTemplates.map((orderTemplate, i, arr) => {
              return (
                <Fragment key={orderTemplate.id + i}>
                  <EditCapacityOrderTemplateListItem
                    key={orderTemplate.id}
                    isIncrementDisabled={isIncrementDisabled}
                    handleIncreaseQty={handleIncreaseQty}
                    handleDecreaseQty={handleDecreaseQty}
                    orderTemplateCapacityCounts={orderTemplateCapacityCounts}
                    orderTemplate={orderTemplate}
                  />
                  {i !== arr.length - 1 && <hr />}
                </Fragment>
              );
            })}
          </Box>
        </Box>
      </Box>
    </MiModal>
  );
};

const EditCapacityOrderTemplateListItem = ({
  isIncrementDisabled,
  handleIncreaseQty,
  handleDecreaseQty,
  orderTemplateCapacityCounts,
  orderTemplate,
}) => {
  const chipBg = orderTemplate.color || "grey.medium";
  const chipFg = getContrastColor(chipBg);

  return (
    <Box display="flex" alignItems="center" gap={4}>
      <MiAvatar
        sx={{
          bgcolor: chipBg,
          color: chipFg,
          borderRadius: 0.5,
          fontSize: "0.75rem",
          height: 28,
          width: 28,
        }}
        variant="square"
        data-testid="attempt-type"
      >
        {orderTemplate.shortName}
      </MiAvatar>
      <Typography noWrap variant="BM2" sx={{ flex: 1, minWidth: 0 }}>
        {orderTemplate.name}
      </Typography>
      <QuantityControl
        isDecreaseDisabled={
          (orderTemplateCapacityCounts[orderTemplate.id] || 0) === 0
        }
        isIncreaseDisabled={isIncrementDisabled}
        onIncreaseQty={(e) => handleIncreaseQty(e, orderTemplate)}
        onDecreaseQty={(e) => handleDecreaseQty(e, orderTemplate)}
        quantity={orderTemplateCapacityCounts[orderTemplate.id] || 0}
        variant="boxed"
      />
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  modalContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
}));
